import React, { FCC, ReactNode, useState } from 'react'
import { useLocalStorage } from 'react-use'

import { useGetUnreadNotificationsCount, useFlag } from '@sketch/modules-common'

import { useThemeContext } from '@sketch/global-styles'

import { localStorageKeys } from '@sketch/constants'

import { userIsEditorOrAdmin } from '../../utils'
import useGetActiveProjectInRoute from '../../../projects/hooks/useGetActiveProjectInRoute'

import * as Collapsible from '@radix-ui/react-collapsible'
import { Box, UnstyledList } from '@sketch/components'
import UserNavbarSection from '../../../user/components/UserNavbarSection'
import { ProjectsMenu } from '../../../projects/components/ProjectsMenu'

import { WorkspacesDropdown } from '../../containers/WorkspacesDropdown'
import { MenuItem, getWorkspaceMenuItems } from './menuItems'
import BottomMenu from './components/BottomMenu/BottomMenu'

import {
  SidebarWrapper,
  WorkspaceSelectorWrapper,
  MenuWrapper,
  Header,
  FooterWrapper,
  CollapsibleIcon,
  MenuListItem,
} from './Sidebar.styles'

// GQL
import {
  GetProjectsQuery,
  useGetInitialUserQuery,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'

import { WorkspaceSidebarNotice } from '../WorkspaceSidebarNotice'
import { useCleanupRecentlyOpenedSharesStore } from '../../../shares/hooks/useRecentlyOpenedSharesStore'

interface SidebarProps {
  workspace: WorkspaceMinimalFragment
}

// Helper Components
const renderMenuItems = (menuItems: MenuItem[]): ReactNode => (
  <UnstyledList role={!menuItems.length ? undefined : 'menu'}>
    {menuItems.map(({ key, item, hidden }) =>
      !hidden ? <MenuListItem key={key}>{item}</MenuListItem> : null
    )}
  </UnstyledList>
)

export const Sidebar: FCC<SidebarProps> = ({ workspace }) => {
  const { isDarkMode } = useThemeContext()

  const { data: userData } = useGetInitialUserQuery()

  const localStorageKey = localStorageKeys.sidebarSectionDocumentsCollapsed
  const [isCollapsedDocumentsSection, setIsCollapsedDocumentsSection] =
    useLocalStorage(localStorageKey)

  const activeProject = useGetActiveProjectInRoute()
  const isCurrentProjectArchived = !!activeProject?.archivedAt

  const canUserAdministerShares = userIsEditorOrAdmin(workspace)

  // We do the call here to fetch the new notifications count
  // that we will use from the cache in the LoggedIn UserNavBar section
  useGetUnreadNotificationsCount()

  // Projects
  const isFinance = workspace.userRole === 'FINANCE'
  const isPartner = userData?.me.isPartner

  const showProjectsList = userData && !isFinance && !isPartner

  const [hasDraftsProject, setHasDraftsProject] = useState(false)

  const handleProjectsLoad = (data: GetProjectsQuery) => {
    const draftsProject = data?.workspace.draftsProject.entries[0]
    setHasDraftsProject(Boolean(draftsProject))
  }

  const isRecentlyOpenedEnabled = useFlag('recently-opened-shares')

  useCleanupRecentlyOpenedSharesStore(isRecentlyOpenedEnabled)

  const workspaceMenuItems = getWorkspaceMenuItems({
    workspace,
    hasDraftsProject,
    canUserAdministerShares,
    isArchiveFeatureEnabled: workspace.features.archiveEnabled,
    isCurrentProjectArchived,
    isRecentlyOpenedEnabled,
  })

  const hasVisibleMenuitems = !workspaceMenuItems.every(item => item.hidden)

  const handleCollapsibleClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    setIsCollapsedDocumentsSection(!isCollapsedDocumentsSection)
  }

  return (
    <SidebarWrapper>
      <WorkspaceSelectorWrapper>
        <WorkspacesDropdown workspace={workspace} />
        <UserNavbarSection workspace={workspace} />
      </WorkspaceSelectorWrapper>
      <WorkspaceSidebarNotice workspace={workspace} />
      <MenuWrapper data-testid="sidebar-projects" $isDarkMode={isDarkMode}>
        {!isPartner && !isFinance && hasVisibleMenuitems && (
          <>
            <Collapsible.Root
              data-testid="documents"
              className="CollapsibleRoot"
              open={!isCollapsedDocumentsSection}
              onOpenChange={
                setIsCollapsedDocumentsSection as (isOpen: boolean) => void
              }
            >
              <Header>
                Documents
                <Collapsible.Trigger
                  data-testid="collapse-documents-button"
                  asChild
                >
                  <CollapsibleIcon
                    isOpen={!isCollapsedDocumentsSection}
                    onClick={handleCollapsibleClick}
                  />
                </Collapsible.Trigger>
              </Header>
              {!isCollapsedDocumentsSection ? (
                <Collapsible.Content data-testid="documents-content">
                  {renderMenuItems(workspaceMenuItems)}
                </Collapsible.Content>
              ) : (
                <>
                  {renderMenuItems(
                    workspaceMenuItems.filter((item: MenuItem) => item.isActive)
                  )}
                </>
              )}
            </Collapsible.Root>
          </>
        )}

        <Box mb={4} />

        {showProjectsList && (
          <ProjectsMenu
            workspace={workspace}
            onLoadCompleted={handleProjectsLoad}
          />
        )}
      </MenuWrapper>
      <FooterWrapper>
        <BottomMenu workspace={workspace} />
      </FooterWrapper>
    </SidebarWrapper>
  )
}
